import amazonSvg from '@/assets/icons/retailers/amazon.svg'
import amazonDarkSvg from '@/assets/icons/retailers/amazon-dark.svg'
import bestbuySvg from '@/assets/icons/retailers/bestbuy.svg'
import bestbuyDarkSvg from '@/assets/icons/retailers/bestbuy-dark.svg'
import costcoSvg from '@/assets/icons/retailers/costco.svg'
import ebaySvg from '@/assets/icons/retailers/ebay.svg'
import etsySvg from '@/assets/icons/retailers/etsy.svg'
import googleShoppingSvg from '@/assets/icons/retailers/google-shopping.svg'
import homedepotSvg from '@/assets/icons/retailers/homedepot.svg'
import ikeaSvg from '@/assets/icons/retailers/ikea.svg'
import macysSvg from '@/assets/icons/retailers/macys.svg'
import macysDarkSvg from '@/assets/icons/retailers/macys-dark.svg'
import nikeSvg from '@/assets/icons/retailers/nike.svg'
import targetSvg from '@/assets/icons/retailers/target.svg'
import targetDarkSvg from '@/assets/icons/retailers/target-dark.svg'
import tjMaxxSvg from '@/assets/icons/retailers/tjmaxx.svg'
import walgreensSvg from '@/assets/icons/retailers/walgreens.svg'
import walmartSvg from '@/assets/icons/retailers/walmart.svg'
import wayfairSvg from '@/assets/icons/retailers/wayfair.svg'
import wishSvg from '@/assets/icons/retailers/wish.svg'
import {Theme} from '@/lib/theme/theme'
import {useCurrentTheme} from '@/lib/theme/useCurrentTheme'
import {isSupportedRetailer, type Product, Retailer} from '@/models/productModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './ProductCard.module.scss'

const RETAILER_IMG: Record<Retailer, string> = {
  [Retailer.Walmart]: walmartSvg,
  [Retailer.Amazon]: amazonSvg,
  [Retailer.BestBuy]: bestbuySvg,
  [Retailer.GoogleShopping]: googleShoppingSvg,
  [Retailer.Costco]: costcoSvg,
  [Retailer.Etsy]: etsySvg,
  [Retailer.eBay]: ebaySvg,
  [Retailer.HomeDepot]: homedepotSvg,
  [Retailer.Wayfair]: wayfairSvg,
  [Retailer.IKEA]: ikeaSvg,
  [Retailer.TJMaxx]: tjMaxxSvg,
  [Retailer.Walgreens]: walgreensSvg,
  [Retailer.Nike]: nikeSvg,
  [Retailer.Macys]: macysSvg,
  [Retailer.Wish]: wishSvg,
  [Retailer.Target]: targetSvg,
}

const RETAILER_IMG_DARKMODE: Record<Retailer, string> = {
  [Retailer.Walmart]: walmartSvg,
  [Retailer.Amazon]: amazonDarkSvg,
  [Retailer.BestBuy]: bestbuyDarkSvg,
  [Retailer.GoogleShopping]: googleShoppingSvg,
  [Retailer.Costco]: costcoSvg,
  [Retailer.Etsy]: etsySvg,
  [Retailer.eBay]: ebaySvg,
  [Retailer.HomeDepot]: homedepotSvg,
  [Retailer.Wayfair]: wayfairSvg,
  [Retailer.IKEA]: ikeaSvg,
  [Retailer.TJMaxx]: tjMaxxSvg,
  [Retailer.Walgreens]: walgreensSvg,
  [Retailer.Nike]: nikeSvg,
  [Retailer.Macys]: macysDarkSvg,
  [Retailer.Wish]: wishSvg,
  [Retailer.Target]: targetDarkSvg,
}

const RETAILER_TEXT: Record<Retailer, string | null> = {
  [Retailer.Walmart]: null,
  [Retailer.Amazon]: null,
  [Retailer.BestBuy]: null,
  [Retailer.GoogleShopping]: 'Google Shopping',
  [Retailer.Costco]: null,
  [Retailer.Etsy]: null,
  [Retailer.eBay]: null,
  [Retailer.HomeDepot]: 'Home Depot',
  [Retailer.Wayfair]: null,
  [Retailer.IKEA]: null,
  [Retailer.TJMaxx]: null,
  [Retailer.Walgreens]: null,
  [Retailer.Nike]: null,
  [Retailer.Macys]: null,
  [Retailer.Wish]: null,
  [Retailer.Target]: null,
}

interface Props {
  retailer: Product['retailer']
  seller?: Product['seller']
  shouldDisplayText?: boolean
}

export default memo<Props>(function ProductRetailerImage({seller, retailer, shouldDisplayText}) {
  const realRetailer = seller ?? retailer
  const [currentTheme] = useCurrentTheme()

  if (isSupportedRetailer(realRetailer)) {
    // eslint-disable-next-line security/detect-object-injection -- its safe
    if (RETAILER_IMG[realRetailer] && RETAILER_IMG_DARKMODE[realRetailer])
      return (
        <>
          <img
            height={16}
            src={
              currentTheme === Theme.Dark
                ? RETAILER_IMG_DARKMODE[realRetailer]
                : RETAILER_IMG[realRetailer]
            }
            loading='lazy'
            alt=''
          />
          <VisuallyHidden>{`Retailer: ${realRetailer}`}</VisuallyHidden>
          {shouldDisplayText && !!RETAILER_TEXT[realRetailer] && (
            <span aria-hidden>{RETAILER_TEXT[realRetailer]}</span>
          )}
        </>
      )
    return null
  }
  return (
    <div className={style.UnsupportedRetailer}>
      <VisuallyHidden>Retailer: {realRetailer}</VisuallyHidden>
      <span aria-hidden>{realRetailer.toUpperCase()}</span>
    </div>
  )
})
